import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, X, Layers, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import { getStages, getDevelopers } from "src/features/MiniCRM/CachedData";
import DeleteLeadsDialog from "../DeleteLeadsDialog";
import BulkActionsMenu from "./BulkActionsMenu";
import BulkActionOptionsDropdown from "./BulkActionOptionsDropdown";
import LeadActivityModal from "../../LeadDetails/LeadActivityModal";

export default function BulkActions({ selectedLeadIds = [], setSelectedLeadIds, workspaceId, canDelete = false, onDeleted }) {
    const [deleteIds, setDeleteIds] = useState(null);
    const [bulkActionOpen, setBulkActionOpen] = useState(false);
    const [developers, setDevelopers] = useState([]);
    const [stages, setStages] = useState([]);
    const [bulkAction, setBulkAction] = useState(null);
    const [selectedActivity, setSelectedActivity] = useState(null);

    const bulkActionsRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target)) {
                setBulkActionOpen(false);
                setBulkAction(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const loadBulkActionData = async () => {
            try {
                const [developersData, stagesData] = await Promise.all([
                    getDevelopers(workspaceId),
                    getStages(),
                ]);
                setDevelopers(developersData || []);
                setStages(stagesData || []);
            } catch (error) {
                console.error("Failed to load bulk action data:", error);
            }
        };

        loadBulkActionData();
    }, [workspaceId]);

    const handleBulkAction = (action) => {
        if (action === "DELETE" && canDelete) {
            setDeleteIds([...selectedLeadIds]); setBulkActionOpen(false); setBulkAction(null); return;
        }
        const submenuActions = [
            "ASSIGN_USER",
            "CHANGE_STAGE",
            "CHANGE_STATUS",
            "CHANGE_PRIORITY",
            "ADD_TAG",
            "REPLACE_TAG",
            "ACTIVITY",
        ];

        if (submenuActions.includes(action)) {
            setBulkAction(action);
            return;
        }

        // Direct action handling
        setBulkActionOpen(false);
        setBulkAction(null);
        toast.info("This bulk action is not available yet.");
    };

    const handleBulkActionValue = (value) => {
        if (bulkAction === "ACTIVITY") {
            setSelectedActivity(value);
            setBulkAction(null);
            setBulkActionOpen(false);
            return;
        }

        toast.info("This bulk action is not available yet.");
        setBulkAction(null);
        setBulkActionOpen(false);
    };

    const clearSelection = () => {
        setSelectedLeadIds([]);
        setBulkAction(null);
        setBulkActionOpen(false);
    };

    if (selectedLeadIds.length === 0) return null;

    return (
        <div className="crm-selection-bar flex items-center gap-2">
            {/* Selection summary */}
            <div className="flex h-9 items-center gap-2 rounded-xl border border-[#d6e5eb] bg-[#f5fafc] px-2.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-md bg-[#e4f4f9]">
                    <CheckCircle2 size={13} strokeWidth={2} className="text-[#2f84bd]"/>
                </span>

                <span className="text-[13px] font-semibold text-[#405c6d]">
                    {selectedLeadIds.length}
                </span>

                <span className="text-[13px] font-medium text-[#8197a4]">
                    selected
                </span>
            </div>

            {/* Bulk actions */}
            <div ref={bulkActionsRef} className="relative"
            >
                <button
                    type="button"
                    onClick={() => {
                        setBulkActionOpen((prev) => !prev);

                        if (bulkActionOpen) {
                            setBulkAction(null);
                        }
                    }}
                    className={`flex h-9 items-center gap-2 rounded-xl border px-3 text-[13px] font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#50b8e7]/30
                    ${bulkActionOpen
                            ? "border-[#a9d6e7] bg-[#edf8fb] text-[#236fa3]"
                            : "border-[#d6e5eb] bg-white text-[#526e7e] shadow-sm hover:border-[#a9cad9] hover:bg-[#f5fafc]"
                        }
                `}
                >
                    <Layers size={15} strokeWidth={1.8} className={bulkActionOpen ? "text-[#2f84bd]" : "text-[#6d8493]"}/>
                    <span>Bulk actions</span>

                    <ChevronDown
                        size={13}
                        strokeWidth={1.8}
                        className={`ml-0.5 text-[#8fa5b1] transition-transform duration-150 ${bulkActionOpen ? "rotate-180 text-[#2f84bd]" : ""}`}
                    />
                </button>

                {/* Flyout */}
                {(bulkActionOpen || bulkAction) && (
                    <div className="crm-bulk-flyout absolute left-0 z-40 flex items-start gap-px">
                        <BulkActionsMenu
                            canDelete={canDelete}
                            activeAction={bulkAction}
                            selectedCount={selectedLeadIds.length}
                            onAction={handleBulkAction}
                        />

                        {bulkAction && (
                            <BulkActionOptionsDropdown
                                action={bulkAction}
                                developers={developers}
                                stages={stages}
                                onSelect={handleBulkActionValue}
                            />
                        )}
                    </div>
                )}
            </div>

            {canDelete && <button type="button" className="crm-button crm-delete-selection" onClick={() => handleBulkAction("DELETE")}><Trash2 size={15} />Delete</button>}
            {/* Clear selection */}
            <button
                type="button"
                onClick={clearSelection}
                title="Clear selection"
                className="flex h-9 w-9 items-center justify-center rounded-xl text-[#8197a4] transition-colors hover:bg-[#edf5f8] hover:text-[#405c6d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bddce8]">
                <X size={15} strokeWidth={1.8}/>
            </button>

            {deleteIds && <DeleteLeadsDialog leadIds={deleteIds} workspaceId={workspaceId} onClose={() => setDeleteIds(null)} onDeleted={() => { setDeleteIds(null); clearSelection(); onDeleted?.(); }} />}
            {/* Activity modal */}
            {selectedActivity && (
                <LeadActivityModal
                    leadId={selectedLeadIds[0]}
                    type={selectedActivity}
                    onClose={() => setSelectedActivity(null)}
                    onSuccess={() => {
                        setSelectedActivity(null);
                        toast.success(
                            `${selectedLeadIds.length} lead(s) activity logged`
                        );
                    }}
                />
            )}
        </div>
    );
}
